import {useState} from 'react';

import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import {Box, Card, CardContent, CardMedia, Grid, IconButton, Typography} from '@mui/material';

import {CompanyProfile} from '../types';

import CompanyProfileHeaderLogo from './CompanyProfileHeaderLogo';
import {CompanyProfileHeaderForm} from './forms/CompanyProfileHeaderForm';
import {PROFILE_DIALOG_SX, PROFILE_DIALOG_TITLE_SX} from './profileDialogStyles';
import EmpionDialog from '../../../../components/EmpionDialog/EmpionDialog';
import {orangeColors} from '../../../../layouts/theme/designSystem';

type CompanyProfileHeaderPropsType = {
  company: CompanyProfile;
  updateCompany: (data: CompanyProfile) => void;
  updateCompanyLogo(logoId: string | null): void;
  companyHeaderImgUrl: string | undefined;
};

export const CompanyProfileHeader = ({
  company,
  updateCompany,
  updateCompanyLogo,
  companyHeaderImgUrl,
}: CompanyProfileHeaderPropsType) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {t} = useTranslation();

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleFormSubmit = (data: CompanyProfile) => {
    updateCompany(data);
    toggleModal();
  };

  const handleFormCancel = () => {
    toggleModal();
  };

  const createExternalLink = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `https://${url}`;
  };

  const renderHeaderInformation = () => {
    return (
      <>
        {company.slogan?.de || company.slogan?.en || company.industry?.de || company.industry?.en ? (
          <>
            <Typography variant='h1'>{company?.name}</Typography>
            <Typography variant='h4'>{company?.slogan?.de}</Typography>
            <Typography variant='h4'>{company?.slogan?.en}</Typography>
            <Typography variant='h4' sx={{color: 'gray'}}>
              {company?.industry?.de}
            </Typography>
            <Typography variant='h4' sx={{color: 'gray'}}>
              {company?.industry?.en}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant='h1'>{company?.name}</Typography>
            <Grid mt={4} mb={2} sx={{opacity: 0.3, textAlign: 'center'}}>
              <div dangerouslySetInnerHTML={{__html: t('companyProfile.header.emptyState') as string}} />
            </Grid>
          </>
        )}
      </>
    );
  };

  const renderHeaderImage = () => {
    return (
      <Grid container style={{height: '200px'}}>
        {companyHeaderImgUrl ? (
          <CardMedia<'img'>
            component='img'
            height='100%'
            image={companyHeaderImgUrl}
            alt={t('companyProfile.header.bannerAlt')}
            sx={{
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        ) : (
          <Grid
            item
            xs={12}
            height={'200px'}
            sx={{
              backgroundColor: orangeColors[4],
            }}
          >
            <Grid sx={{padding: {xs: 2, md: 8}, opacity: 0.5, textAlign: 'center'}}>
              <div dangerouslySetInnerHTML={{__html: t('companyProfile.header.media.images.headerImage') as string}} />
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box data-tour='profile.header' sx={{mb: 3}}>
      <Card
        elevation={0}
        sx={{
          backgroundImage: 'none',
          borderRadius: '16px',
          border: `1px solid ${orangeColors[3]}`,
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        {renderHeaderImage()}
        <CardContent>
          <Grid container display={'flex'} flexDirection={'row'}>
            <Grid
              item
              xs={12}
              md={3}
              sx={{marginTop: '-70px'}}
              display={'flex'}
              justifyContent={'center'}
              flexDirection={'column'}
            >
              <CompanyProfileHeaderLogo company={company} updateCompanyLogo={updateCompanyLogo} />
            </Grid>
            <Grid item xs={12} md={7} sx={{flexGrow: 1}}>
              {renderHeaderInformation()}
            </Grid>
            <Grid container item xs={12} md={2} justifyContent='flex-end'>
              <Grid container justifyContent='flex-end' alignItems='flex-start'>
                <IconButton
                  disabled={!company.websiteUrl}
                  component={Link}
                  to={createExternalLink(company.websiteUrl ?? '')}
                  target='_blank'
                >
                  <LanguageIcon />
                </IconButton>
                <IconButton
                  disabled={!company.linkedinUrl}
                  component={Link}
                  to={createExternalLink(company.linkedinUrl ?? '')}
                  target='_blank'
                >
                  <LinkedInIcon />
                </IconButton>
                <IconButton
                  color='primary'
                  disabled={!company.xingUrl}
                  component={Link}
                  to={createExternalLink(company.xingUrl ?? '')}
                  target='_blank'
                >
                  <LanguageIcon />
                </IconButton>
              </Grid>
            </Grid>
            {/* Edit button that opens modal */}
            <Grid item xs={12} display={'flex'} justifyContent={'flex-end'}>
              <IconButton onClick={toggleModal}>
                <EditIcon />
              </IconButton>
            </Grid>
          </Grid>
          <EmpionDialog
            open={isModalOpen}
            onClose={toggleModal}
            maxWidth='md'
            showCloseButton
            title={t('companyProfile.header.dialog.title')}
            sxTitle={PROFILE_DIALOG_TITLE_SX}
            sx={PROFILE_DIALOG_SX}
          >
            <CompanyProfileHeaderForm company={company} onCancel={handleFormCancel} onSubmit={handleFormSubmit} />
          </EmpionDialog>
        </CardContent>
      </Card>
    </Box>
  );
};
